import merge from "lodash/merge";
import { getCurrentCulture} from "@utils";
import { redirect } from "@state";
import { isAbortedRequest } from "@utils/http/isAbortedRequest";
import { HttpClientRequestInit } from "@data-types/http/httpClientRequestInit";
import { createAbortError } from "@utils/http/createAbortError";
import { logger } from "@utils/logger/logger";

export class MainHttpClient {
	defaultHeaders: { "Caller-Environment": string };

	constructor() {
		this.defaultHeaders = {
			"Caller-Environment": WEBPACK_ENVIRONMENT_KEY,
		};
		this._internalRequest = this._internalRequest.bind(this);
		this.request = this.request.bind(this);
		this.get = this.get.bind(this);
		this.post = this.post.bind(this);
		this.put = this.put.bind(this);
		this.delete = this.delete.bind(this);
	}

	_internalRequest(
		url: RequestInfo | URL,
		options?: HttpClientRequestInit
	): Promise<Response> {
		return fetch(url, options);
	}

	addAdditionalQueryParamsToRequest(url: URL | RequestInfo) {
		if (!WEBPACK_SHOULD_SET_REQUESTID) {
			return url;
		}

		if (!url || App?.state?.rootStore == null) {
			return url;
		}

		const requestIdValue = `${WEBPACK_ENVIRONMENT_KEY}-${
			App.state.rootStore.uniqueId
		}-${Date.now()}`;

		if (typeof url == "object") {
			(url as URL).searchParams.append("reqId", requestIdValue);
			return url;
		}

		if (url.includes("?")) {
			url = url += "&" + "reqId=" + requestIdValue;
		} else {
			url = url += "/?" + "reqId=" + requestIdValue;
		}
		return url;
	}

	async request(
		url: RequestInfo | URL,
		options?: HttpClientRequestInit,
		unauthRedirect?: string
	) {
		let rawResponse: Response;
		try {
			rawResponse = await this._internalRequest(
				this.addAdditionalQueryParamsToRequest(url),
				options
			);
		} catch (err) {
			if (isAbortedRequest(err)) {
				throw createAbortError(err);
			}
			throw err;
		}

		if (rawResponse.redirected) {
			window.location.href = rawResponse.url;
			return;
		}

		if (rawResponse.status === 308) {
			const body = await rawResponse.json();

			window.location.href = window.location.origin + body.url;
			return;
		}

		if (rawResponse.status === 204) {
			return null;
		}

		if (rawResponse.status === 401 || rawResponse.status === 403) {
			if (unauthRedirect) {
				redirect(`/${getCurrentCulture()}/${unauthRedirect}`);
			} else {
				App.state.rootStore.userAuthStore.removeUserFromLocalStorage();

				const response = await rawResponse.json();
				if (
					response?.errorCode != null &&
					response.errorCode === 400239
				) {
					redirect(
						`/${getCurrentCulture()}/auth/login?isAutomaticLogout=true`
					);
				} else {
					redirect(
						`/${getCurrentCulture()}/auth/login?isSessionExpired=true`
					);
				}
				throw new Error("Unauthorized");
			}
		}

		if (options?.responseType === "raw") {
			return rawResponse;
		}

		let response: any;

		const responseType = this.getResponseTypeFromContentType(rawResponse);

		switch (responseType) {
			case "text": {
				const text = await rawResponse.text();
				response = text.trim() ? text : null;
				break;
			}
			case "blob": {
				const blob = await rawResponse.blob();
				response = blob.size > 0 ? blob : null;
				break;
			}
			case "arrayBuffer": {
				const buffer = await rawResponse.arrayBuffer();
				response = buffer.byteLength > 0 ? buffer : null;
				break;
			}
			case "formData": {
				const formData = await rawResponse.formData();
				response = [...formData.entries()].length > 0 ? formData : null;
				break;
			}
			case "json": {
				const text = await rawResponse.text();
				response = text.trim() ? JSON.parse(text) : null;
				break;
			}
		}

		if (rawResponse.ok) {
			return response;
		}

		// if we got here, error happened
		// throw what we got from the response
		throw {
			statusCode: rawResponse.status,
			data: response,
			rawResponse: rawResponse,
		};
	}

	get(
		url: RequestInfo | URL,
		headers?: HeadersInit,
		options: HttpClientRequestInit = {},
		unauthRedirectUrl?: string
	): Promise<any> {
		let innerHeaders = {
			...this.defaultHeaders,
		};
		if (headers) {
			merge(innerHeaders, headers);
		}

		return this.request(
			url,
			{
				...options,
				method: "GET",
				headers: innerHeaders,
			},
			unauthRedirectUrl
		);
	}

	post(
		url: RequestInfo | URL,
		data: BodyInit | null = null,
		headers?: HeadersInit,
		options: HttpClientRequestInit = {}
	) {
		let innerHeaders = {
			...this.defaultHeaders,
		};
		if (headers) {
			merge(innerHeaders, headers);
		}

		return this.request(url, {
			...options,
			method: "POST",
			body: data,
			headers: innerHeaders,
		});
	}

	put(
		url: RequestInfo | URL,
		data: BodyInit | null = null,
		headers?: HeadersInit,
		options: HttpClientRequestInit = {}
	) {
		let innerHeaders = {
			...this.defaultHeaders,
		};

		if (headers) {
			merge(innerHeaders, headers);
		}

		return this.request(url, {
			...options,
			method: "PUT",
			body: data,
			headers: innerHeaders,
		});
	}

	delete(
		url: RequestInfo | URL,
		headers: HeadersInit,
		options: HttpClientRequestInit = {}
	) {
		let innerHeaders = {
			...this.defaultHeaders,
		};

		if (headers) {
			merge(innerHeaders, headers);
		}

		return this.request(url, {
			...options,
			method: "DELETE",
			headers: innerHeaders,
		});
	}

	getResponseTypeFromContentType(rawResponse: Response) {
		const contentType =
			rawResponse.headers.get("Content-Type")?.toLowerCase() || "";

		if (!contentType) {
			logger.logError("Missing Content-Type from request", {
				extra: {
					request_url: rawResponse.url,
					contentType: contentType,
					rawResponse: rawResponse,
				},
			});
		}

		if (
			contentType.includes("application/json") ||
			contentType.includes("+json")
		) {
			return "json";
		}

		if (
			contentType.startsWith("text/") ||
			contentType.includes("application/javascript")
		) {
			return "text";
		}

		if (contentType.includes("multipart/form-data")) {
			return "formData";
		}

		if (
			contentType.includes("application/octet-stream") ||
			contentType.includes("application/pdf") ||
			contentType.includes("application/zip") ||
			contentType.startsWith("audio/") ||
			contentType.startsWith("video/")
		) {
			return "arrayBuffer";
		}

		if (
			contentType.startsWith("image/") ||
			contentType.includes("application/vnd") ||
			contentType.includes("application/x-binary")
		) {
			return "blob";
		}

		return "text";
	}
}

const mainHttpClient = new MainHttpClient();
export { mainHttpClient };
